/**
 * RegenOne — inspector-scoped "regenerate this section" modal.
 *
 * Spec 23 P10.5 + Spec 22 regen-producer MVP. The writer opens this
 * from the Inspector header to capture a regen intent for one
 * section, with optional free-text guidance ("e.g. cite the
 * updated SAP; use per-protocol set not ITT"). Confirm dispatches
 * a ``regen`` EventLog entry — the Spec 22 MVP producer — which
 * ticks the ``regens`` counter used by the ⭐ badge + event legend.
 *
 * SRP: presentation only. The shell owns the actual re-run
 * workflow (re-setting the selection picker with the target module
 * pre-checked, or a future in-place re-run once the state machine
 * supports it). This component captures the writer's decision +
 * guidance text; the shell routes it.
 *
 * # DECISION: single-option MVP — no "include downstream" radio.
 * # WHY: the scenes prototype's "§9.3 + downstream" radio
 * #      hardcodes "§2 · §11 · §12" for CSR; that's doctype-
 * #      specific invention. Real downstream data requires
 * #      per-module dep tracking that neither the planner nor the
 * #      runtime expose today. Rather than faking the list, we
 * #      ship a single-section regen now and grow the radio group
 * #      cleanly when we have a backend signal to populate it.
 * # DATE: 2026-04-22
 *
 * # DECISION: confirm is an EventLog dispatch; the actual re-run
 * #   is the shell's concern.
 * # WHY: Spec 23 §5 Q5 — backend regen signal is future-scope. The
 * #      shell's handler may route the intent to a reset + pre-
 * #      seeded LoomSelection (reusing the existing pipeline), or
 * #      to a future direct re-run when that lands. The modal
 * #      stays honest about WHAT was captured (decision + text)
 * #      without committing to HOW it re-runs.
 * # DATE: 2026-04-22
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useState } from "react";
import { brandColors, fonts, gateColorAt, inkColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegenTarget {
  /** Section id for the EventLog entry + the eventual re-run's
   *  ``module_ids``. */
  sectionId: string;
  /** Human-readable section number for display ("9.3"). */
  sectionNumber: string;
  /** Human-readable title — "Primary endpoint analysis". */
  sectionTitle: string;
  /** Gate name ("assemble" / "configure" / …) for colour + context. */
  gateName: string;
  /** Display label for the gate — "Assemble". */
  gateDisplayName: string;
  /** Gate's palette index (position in config.gates) so the modal
   *  uses the same colour the spine uses for this gate. */
  gatePaletteIndex: number;
  /** Rough estimate — "~8m" drives the "this will take" copy.
   *  Derived from ``estMinutes(node)`` by the caller. */
  estMinutes: number;
}

export interface RegenConfirmation {
  sectionId: string;
  /** Writer's free-text guidance. Null when empty — the shell
   *  shouldn't ship an empty string as the prompt. */
  guidance: string | null;
}

export interface RegenOneProps {
  target: RegenTarget;
  /** Writer clicked Regenerate — shell dispatches the regen event +
   *  routes to the re-run workflow. */
  onConfirm: (confirmation: RegenConfirmation) => void;
  /** Writer clicked Cancel or × — just close. */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    background: "rgba(15, 10, 30, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: fonts.body,
  },
  modal: {
    width: "100%",
    maxWidth: "420px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    background: surfaceColors.paper,
    borderRadius: "6px",
    boxShadow: "0 20px 48px rgba(15, 10, 30, 0.3)",
    overflow: "hidden",
  },
  header: {
    padding: "12px 14px",
    background: "#fff",
    borderBottom: `1px solid ${surfaceColors.line}`,
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  headerMeta: {
    flex: 1,
    minWidth: 0,
  },
  headerTag: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  headerTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: inkColors.ink,
    marginTop: "2px",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "18px",
    color: inkColors.ink4,
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: 1,
    "&:hover": { color: inkColors.ink },
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: "16px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  card: {
    padding: "14px",
    background: "#fff",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
  },
  cardLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: inkColors.ink,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    marginBottom: "8px",
    display: "block",
  },
  cardBody: {
    fontSize: "12px",
    color: inkColors.ink2,
    lineHeight: 1.5,
  },
  cardMeta: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    marginTop: "4px",
  },
  guidanceLabel: {
    fontSize: "10.5px",
    fontWeight: 700,
    color: inkColors.ink3,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    marginBottom: "6px",
    display: "block",
  },
  textarea: {
    width: "100%",
    minHeight: "64px",
    padding: "8px",
    fontSize: "12px",
    fontFamily: fonts.body,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    resize: "vertical",
    color: inkColors.ink,
    background: surfaceColors.paper,
    boxSizing: "border-box",
    lineHeight: 1.5,
    "&:focus": {
      outline: "none",
      // Use the full border shorthand so Griffel doesn't warn that
      // borderColor alone overrides an already-declared border.
      border: `1px solid ${brandColors.primary}`,
    },
  },
  footer: {
    padding: "12px 14px",
    background: "#fff",
    borderTop: `1px solid ${surfaceColors.line}`,
    display: "flex",
    gap: "8px",
  },
  cancelButton: {
    padding: "9px 14px",
    fontSize: "11.5px",
    fontWeight: 600,
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: "#fff",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
  },
  primaryButton: {
    flex: 1,
    padding: "9px 14px",
    fontSize: "11.5px",
    fontWeight: 700,
    borderRadius: "6px",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
  },
});

// ---------------------------------------------------------------------------
// Pure helper (exported for tests)
// ---------------------------------------------------------------------------

/**
 * Estimate copy for the primary button — "Regenerate §9.3 · ~8m".
 * Pure so the grammar is testable. We include the estimate in the
 * button so the writer sees the cost before clicking.
 */
export function formatRegenButton(target: RegenTarget): string {
  return `Regenerate §${target.sectionNumber} · ~${target.estMinutes}m`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RegenOne: React.FC<RegenOneProps> = ({ target, onConfirm, onCancel }) => {
  const styles = useStyles();
  const [guidance, setGuidance] = useState<string>("");
  const gateColor = gateColorAt(target.gatePaletteIndex);

  const handleConfirm = useCallback(() => {
    const trimmed = guidance.trim();
    onConfirm({
      sectionId: target.sectionId,
      guidance: trimmed.length > 0 ? trimmed : null,
    });
  }, [guidance, onConfirm, target.sectionId]);

  return (
    <dialog
      open
      className={styles.backdrop}
      aria-modal="true"
      aria-label={`Regenerate section ${target.sectionNumber}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerMeta}>
            <div className={styles.headerTag} style={{ color: gateColor }}>
              §{target.sectionNumber} · {target.gateDisplayName}
            </div>
            <div className={styles.headerTitle}>{target.sectionTitle}</div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onCancel} aria-label="Close regenerate dialog">
            ×
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.card}>
            <span className={styles.cardLabel}>Regenerate this section</span>
            <div className={styles.cardBody}>
              Re-drafts §{target.sectionNumber} from scratch. The current draft stays in the document until the new one
              lands; you can compare and revert if needed.
            </div>
            <div className={styles.cardMeta}>
              ~{target.estMinutes}m · scoped to §{target.sectionNumber}
            </div>
          </div>

          <div className={styles.card}>
            <label className={styles.guidanceLabel} htmlFor="regen-guidance">
              Guidance (optional)
            </label>
            <textarea
              id="regen-guidance"
              className={styles.textarea}
              placeholder="e.g. cite the updated SAP; use per-protocol set not ITT"
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              aria-label="Guidance for the regeneration"
            />
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            style={{
              background: gateColor,
              boxShadow: `0 2px 6px ${gateColor}44`,
            }}
            onClick={handleConfirm}
          >
            {formatRegenButton(target)}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default RegenOne;
